import axios from 'axios';
import jwtDecode from 'jwt-decode';
import FuseUtils from '@fuse/utils/FuseUtils';

class JwtService extends FuseUtils.EventEmitter {
	init() {
		this.setAxiosInterceptors();
		this.handleAuthentication();
	}

	isFetchingAccessToken: boolean = false;

	waitingApiCalls: any[] = [];

	isAuthTokenValid = token => {
		if (!token) return false;

		const decoded = jwtDecode<any>(token);
		const currentTime = Date.now() / 1000;

		if (decoded.exp < currentTime) return false;

		return true;
	};

	getAccessToken = () => {
		return window.localStorage.getItem('accessToken');
	};

	getRefreshToken = () => {
		return window.localStorage.getItem('refreshToken');
	};

	setAccessToken = (accessToken: string | null) => {
		if (accessToken != null) localStorage.setItem('accessToken', accessToken);
		else localStorage.removeItem('accessToken');

		this.setAxiosAuthorizationHeader(accessToken);
	};

	setRefreshToken = (refreshToken: string | null) => {
		if (refreshToken !== null) localStorage.setItem('refreshToken', refreshToken);
		else localStorage.removeItem('refreshToken');
	};

	clearFilters = () => {
		const filters: string[] = [];
		for (let index = 0; index < localStorage.length; index += 1) {
			const filter = localStorage.key(index);
			if (filter?.includes('PageFilters')) filters.push(filter);
		}
		filters.forEach(filter => localStorage.removeItem(filter));
	};

	logout = () => {
		this.setAccessToken(null);
		this.setRefreshToken(null);
		this.clearFilters();
	};

	autoLogout = (message?: string) => {
		this.emit('onAutoLogout', message);
		// this.emit('onAutoLogout', message || 'Session expired');
		this.logout();
	};

	setAxiosAuthorizationHeader = (accessToken: string | null) => {
		if (accessToken !== null) axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
		else delete axios.defaults.headers.common.Authorization;
	};

	setAxiosInterceptors = () => {
		axios.interceptors.response.use(
			response => {
				return response;
			},
			error => {
				return new Promise((resolve, reject) => {
					const request = error.config;

					if (error.response?.status === 401) {
						if (!request.headers.__isRefresh) {
							if (!this.isFetchingAccessToken) {
								this.isFetchingAccessToken = true;

								this.refreshTokens().then(({ accessToken }) => {
									this.isFetchingAccessToken = false;
									this.waitingApiCalls = this.waitingApiCalls.filter(callback =>
										callback(accessToken)
									);
								});
							}

							return this.waitingApiCalls.push(accessToken => {
								request.headers.Authorization = `Bearer ${accessToken}`;
								return resolve(axios(request));
							});
						}

						return reject();
					}

					return reject(error);
				});
			}
		);
	};

	// Checks if accessToken is valid when page changes and tries to refresh token if invalid
	handleAuthentication = () => {
		const accessToken = this.getAccessToken();
		const refreshToken = this.getRefreshToken();

		if (!this.isAuthTokenValid(accessToken)) {
			if (this.isAuthTokenValid(refreshToken)) {
				this.refreshTokens();
			} else this.autoLogout();
		} else {
			this.setAxiosAuthorizationHeader(accessToken);
			this.emit('onAutoLogin', true);
		}
	};

	signInWithEmailAndPassword = (mail: string, pass: string, app: string, redirectToAnotherApp?: boolean) => {
		return new Promise<any>((resolve, reject) => {
			axios
				.post('/api/login', { mail, pass, app })
				.then(response => {
					if (import.meta.env.DEV) console.log('login', response.data);
					const { accessToken, refreshToken, userData } = response.data;

					if (!redirectToAnotherApp) {
						this.setAccessToken(accessToken);
						this.setRefreshToken(refreshToken);
						this.emit('onLogin', userData);
					}

					resolve(response.data);
				})
				.catch(({ response }) => {
					if (!response?.data) {
						if (navigator && navigator.onLine) {
							const message = 'Server connection failure';
							this.emit('onLoginError', message);
							reject(new Error(message));
						} else {
							const message = 'There is a problem with your INTERNET...';
							this.emit('onLoginError', message);
							reject(new Error(message));
						}
					} else {
						const message = response.data.detail;
						this.emit('onLoginError', message);
						reject(new Error(message));
					}
				});
		});
	};

	signInMultipleApps = (mail: string, pass: string) => {
		return new Promise<any>((resolve, reject) => {
			axios
				.post('/api/loginMultiple', { mail, pass })
				.then(response => {
					if (import.meta.env.DEV) console.log('loginMultiple', response.data);
					resolve(response.data);
				})
				.catch(({ response }) => {
					if (!response?.data) {
						if (navigator && navigator.onLine) {
							const message = 'Server connection failure';
							this.emit('onLoginError', message);
							reject(new Error(message));
						} else {
							const message = 'There is a problem with your INTERNET...';
							this.emit('onLoginError', message);
							reject(new Error(message));
						}
					} else {
						const message = response.data.detail;
						this.emit('onLoginError', message);
						reject(new Error(message));
					}
				});
		});
	};

	signInWithToken = () => {
		return new Promise<any>((resolve, reject) => {
			axios
				.get('/api/loginWithToken')
				.then(response => {
					if (import.meta.env.DEV) console.log('loginWithToken', response.data);
					resolve(response.data.userData);
				})
				.catch(error => {
					const message = error?.detail;
					this.autoLogout(message);
					if (message) reject(new Error(message));
				});
		});
	};

	refreshTokens = () => {
		return new Promise<{ accessToken: string; refreshToken: string }>((resolve, reject) => {
			axios
				.get('/api/refreshToken', {
					headers: {
						Authorization: `bearer ${this.getRefreshToken()}`,
						__isRefresh: true
					}
				})
				.then(response => {
					if (import.meta.env.DEV) console.log('refreshTokens', response.data);
					const { accessToken, refreshToken } = response.data;
					this.setAccessToken(accessToken);
					this.setRefreshToken(refreshToken);
					this.emit('onAutoLogin', true);
					resolve(response.data);
				})
				.catch(error => {
					const message = error?.detail;
					this.autoLogout(message);
					if (message) reject(new Error(message));
				});
		});
	};
}

const instance = new JwtService();

export default instance;
